// src/components/Steps/VehicleVerification/VehicleVerification.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { track } from '../../../telemetry/journey.js';
import './VehicleVerification.less';
import { ActionButtonWrapper, EvHeader } from '../../index.js';
import useElementHeight from '../../../hooks/useElementHeight.js';
import { useSession } from '../../../context/SessionContext.jsx';
import { useToast } from '../../../context/ToastContext.jsx';
import {
  getLockoutInfo,
  setLockout,
  clearLockout,
  getRemainingLockoutTime,
  formatLockoutTime,
} from '../../../utils/vehicleVerification.js';
import { verifyVehiclePin } from '../../../api/vehicles.js';
import { resolveVehicleChargingRules } from '../../../utils/billingRules.js';
import {
  AUTHORIZATION_DENIED,
  CARD_FALLBACK_MESSAGE,
  PIN_VERIFY_RATE_LIMITED,
  appliedCardFallback,
  enforcedBillingRules,
  pinRateLimitedMessage,
  pricingSnapshotFromAuthorization,
} from '../../../utils/sessionAuthorization.js';
import { STEPS } from '../../../constants/steps.js';
import { accessOutcomeContinues, isAccessOutcome } from '../../../utils/accessOutcome.js';
import { clearedVehicleState } from '../../../utils/clearedVehicleState.js';
import { useOptionalAuth } from '../../../context/AuthContext.jsx';

// Guest-PIN / token proof errors, as driver copy. Never surfaces the
// account-ownership language: the guest flow is not an ownership check.
const proofErrorMessage = (error, fallback) => {
  if (error === 'GUEST_VEHICLE_PIN_INVALID') return 'The vehicle PIN entered is incorrect.';
  if (error === 'GUEST_VEHICLE_PIN_REQUIRED') return 'Please enter your vehicle PIN to continue.';
  if (error === PIN_VERIFY_RATE_LIMITED) {
    return 'Too many vehicle PIN attempts. Please try again later. You can still charge by paying with a card.';
  }
  return fallback;
};

// A refusal of the vehicle PIN on create / attach (wrong, missing, or too
// many attempts). The step stops on it: it never continues on the session
// as created before the PIN step (card).
const PIN_PROOF_ERRORS = new Set(['GUEST_VEHICLE_PIN_INVALID', 'GUEST_VEHICLE_PIN_REQUIRED', PIN_VERIFY_RATE_LIMITED]);

// One vehicle, one charge (sw-ev-api vehicleChargingGuard): the vehicle is
// already charging, or starting, at another charger.
const VEHICLE_ALREADY_CHARGING = 'VEHICLE_ALREADY_CHARGING';
const VEHICLE_ALREADY_CHARGING_FALLBACK =
  'This vehicle is already charging at another charger. Stop that session before starting a new one.';

/**
 * Signed-in driver, vehicle not linked to their account. The API links an
 * unlinked vehicle itself when the PIN is right and the account's verified
 * email matches the vehicle's owner email; this refusal means it could not
 * (another account owns it, the email is unverified or different, or the
 * link needs support). The server's message says which. Charging on the
 * vehicle's account does not happen, and paying by card is the driver's own
 * choice: never a silent switch.
 */
const VEHICLE_NOT_OWNED_FALLBACK = 'This vehicle is not linked to your GreenCharge account.';
const vehicleNotOwnedMessage = (message) =>
  `${message || VEHICLE_NOT_OWNED_FALLBACK} You can still charge by paying with a card.`;

/**
 * Whether a failed access check is a refusal of the PROOF the driver gave
 * (wrong or missing PIN, expired sign-in), which is shown like a PIN failure.
 * Everything else -- no answer, an older API without the route (404), rate
 * limiting, a server error -- is not a verdict, and charging carries on: the
 * backend still decides when the session is created or the vehicle attached.
 *
 * VEHICLE_NOT_OWNED is not a proof error: it is handled on its own (the
 * vehicle is not linked to the signed-in account; see showVehicleNotOwned).
 */
const isAccessProofError = (access) => {
  if (!access || access.ok) return false;
  if (access.error === 'VEHICLE_NOT_OWNED') return false;
  if (access.httpStatus === 401 || access.httpStatus === 403) return true;
  return access.httpStatus === 400 && access.error === 'GUEST_VEHICLE_PIN_REQUIRED';
};

// Segmented PIN Input Component
const PinInput = ({ value, onChange, disabled, hasError, autoFocus, onSubmit }) => {
  const inputRefs = useRef([]);
  const [pins, setPins] = useState(['', '', '', '']);

  useEffect(() => {
    // Sync with parent value
    const digits = value.split('');
    setPins([
      digits[0] || '',
      digits[1] || '',
      digits[2] || '',
      digits[3] || '',
    ]);
  }, [value]);

  useEffect(() => {
    // Auto-focus first input on mount
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  const handleChange = (index, newValue) => {
    // Only allow single digit
    const digit = newValue.replace(/\D/g, '').slice(-1);
    
    const newPins = [...pins];
    newPins[index] = digit;
    setPins(newPins);
    
    // Update parent
    onChange(newPins.join(''));
    
    // Auto-advance to next box
    if (digit && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (!pins[index] && index > 0) {
        // Move to previous box if current is empty
        e.preventDefault();
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 3) {
      e.preventDefault();
      inputRefs.current[index + 1]?.focus();
    } else if (e.key === 'Enter') {
      // Submit on Enter from any box (keyboard "Go"/"Done" on Android)
      e.preventDefault();
      if (onSubmit) onSubmit();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    const digits = pastedData.split('');
    
    const newPins = ['', '', '', ''];
    digits.forEach((digit, i) => {
      if (i < 4) newPins[i] = digit;
    });
    
    setPins(newPins);
    onChange(newPins.join(''));
    
    // Focus appropriate box
    const focusIndex = Math.min(digits.length, 3);
    inputRefs.current[focusIndex]?.focus();
  };

  return (
    <div className="pin-input-wrapper">
      {pins.map((digit, index) => (
        <input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type="password"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength="1"
          value={digit}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={index === 0 ? handlePaste : undefined}
          className={`pin-box ${hasError ? 'error' : ''} ${digit ? 'filled' : ''}`}
          disabled={disabled}
          aria-label={`PIN digit ${index + 1}`}
          autoComplete="off"
          enterKeyHint="done"
        />
      ))}
    </div>
  );
};

const VehicleVerification = ({ handleIncreaseStep, handleDecreaseStep }) => {
  const { ref, height } = useElementHeight();
  const { state, update, setStep, ensureCustomerSession } = useSession();
  // Signed in, the session is the driver's own: a vehicle they use by its
  // PIN (a fleet vehicle, say) never brings its owner's contact along.
  const signedIn = Boolean(useOptionalAuth()?.isAuthenticated);
  const { showToast } = useToast();
  
  const [pin, setPin] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [error, setError] = useState('');
  const [isLocked, setIsLocked] = useState(false);
  const [lockoutSeconds, setLockoutSeconds] = useState(0);
  const [isVerifying, setIsVerifying] = useState(false);
  const lockoutTimerRef = useRef(null);
  const verificationCancelledRef = useRef(false); // Guard against stale async updates
  
  // Get plate from session state - trim and uppercase for display
  const plateForDisplay = state.licensePlate?.trim().toUpperCase() || '';
  const MAX_ATTEMPTS = 3;
  
  // Helper: Reset PIN verification state
  const resetPinVerificationState = useCallback(() => {
    console.log('[VehicleVerification] Resetting PIN verification state');
    setPin('');
    setError('');
    setIsVerifying(false);
    verificationCancelledRef.current = false;
    // Note: We keep attempts and lockout state as they persist per plate
  }, []);
  
  // Check for existing lockout on mount
  useEffect(() => {
    const lockout = getLockoutInfo(plateForDisplay);
    if (lockout) {
      const remaining = getRemainingLockoutTime(plateForDisplay);
      if (remaining > 0) {
        setIsLocked(true);
        setLockoutSeconds(remaining);
        setAttempts(3); // Max attempts reached
      }
    }
  }, [plateForDisplay]);
  
  // Countdown timer for lockout
  useEffect(() => {
    if (isLocked && lockoutSeconds > 0) {
      lockoutTimerRef.current = setInterval(() => {
        setLockoutSeconds((prev) => {
          const next = prev - 1;
          if (next <= 0) {
            // Lockout expired
            clearLockout(plateForDisplay);
            setIsLocked(false);
            setAttempts(0);
            setError('');
            clearInterval(lockoutTimerRef.current);
            showToast({
              message: 'PIN entry unlocked. You may try again.',
              type: 'success',
            });
            return 0;
          }
          return next;
        });
      }, 1000);
      
      return () => {
        if (lockoutTimerRef.current) {
          clearInterval(lockoutTimerRef.current);
        }
      };
    }
  }, [isLocked, lockoutSeconds, plateForDisplay, showToast]);
  
  // Handle PIN input change
  const pinAttemptsRef = React.useRef(0);
  const handlePinChange = (value) => {
    setPin(value);
    setError(''); // Clear error on new input
  };
  
  /**
   * "Don't have a vehicle PIN? Pay by card instead."
   *
   * A plate that is not registered has no PIN that will ever work, and the
   * screen previously offered no way forward: the driver could only guess
   * until the lockout fired. The plate field on Confirm & Pay is optional —
   * leaving it blank goes to the normal paid flow — so this puts the driver
   * back on that path without making them work out that they should navigate
   * back and clear a field.
   *
   * Direction of travel matters: this moves TOWARD payment, never away from
   * it. Every vehicle-derived billing rule is cleared first, so a plate typed
   * a moment ago cannot leave a FREE or fleet rate behind and let someone
   * charge without paying. `verified` is forced false for the same reason.
   */
  const continueWithoutVehicle = useCallback(() => {
    verificationCancelledRef.current = true;
    // Legacy top-level fields and the unified vehicle object, cleared
    // wholesale rather than patched, so no stale field survives to be read
    // downstream. Shared with the access result step's card path.
    update(clearedVehicleState());
    // AMOUNT_PICKER — the same place a driver lands when they leave the plate
    // field empty on Confirm & Pay.
    handleIncreaseStep();
  }, [update, handleIncreaseStep]);

  /**
   * Send the driver to the access result step with the backend's outcome
   * (platform cleanup, Phase 3). The vehicle's trust markers are put back to
   * "not verified, payment required": nothing is charged to this vehicle's
   * account from here, whatever the PIN check said. The plate and the saved
   * selection stay, so the result step knows which vehicle this was about.
   */
  /**
   * The vehicle is not linked to the signed-in account and the API could not
   * link it. Stay on this step with the reason; nothing is created or
   * attached with the vehicle, and its trust markers go back to "not
   * verified, payment required" so no vehicle rate survives. The driver can
   * pay by card with the link below, or go back.
   */
  const showVehicleNotOwned = (message) => {
    const text = vehicleNotOwnedMessage(message);
    update({
      vehicleVerified: false,
      verifiedPlate: null,
      vehicleId: null,
      billingMode: null,
      rateOverride: null,
      vehicle: {
        ...(state.vehicle || {}),
        vehicleId: null,
        verified: false,
        billingMode: null,
        rateOverride: null,
        paymentRequired: true,
        rateApplied: null,
        ownerName: null,
        ownerEmail: null,
      },
    });
    setPin('');
    setError(text);
    showToast({ message: text, type: 'error', duration: 8000 });
  };

  /**
   * The vehicle is already charging (or starting) elsewhere. Stay on this
   * step with the reason; nothing is created or attached with the vehicle
   * and no vehicle rate survives.
   */
  const showVehicleAlreadyCharging = (message) => {
    const text = message || VEHICLE_ALREADY_CHARGING_FALLBACK;
    update({
      vehicleVerified: false,
      verifiedPlate: null,
      vehicleId: null,
      billingMode: null,
      rateOverride: null,
      vehicle: {
        ...(state.vehicle || {}),
        vehicleId: null,
        verified: false,
        billingMode: null,
        rateOverride: null,
        paymentRequired: true,
        rateApplied: null,
        ownerName: null,
        ownerEmail: null,
      },
    });
    setPin('');
    setError(text);
    showToast({ message: text, type: 'error', duration: 8000 });
  };

  const showAccessResult = (accessOutcome) => {
    update({
      accessOutcome,
      // An attempt already exists and may carry this vehicle: a saved vehicle
      // picked before the session was created, or a vehicle an earlier pass
      // through this step attached. (Read from the state this step rendered
      // with, i.e. before this verification touched it.) The typed-plate
      // journey's session is created without a vehicle and is not one.
      accessOutcomeSessionHasVehicle: Boolean(state.sessionUid)
        && Boolean(state.selectedVehicleId || state.vehicle?.verified || state.vehicleVerified),
      vehicleVerified: false,
      verifiedPlate: null,
      vehicleId: null,
      billingMode: null,
      rateOverride: null,
      vehicle: {
        ...(state.vehicle || {}),
        vehicleId: null,
        verified: false,
        billingMode: null,
        rateOverride: null,
        paymentRequired: true,
        rateApplied: null,
        ownerName: null,
        ownerEmail: null,
      },
    });
    setStep(STEPS.FLEET_ACCESS_RESULT);
  };

  // Handle verify button click
  const handleVerify = async () => {
    pinAttemptsRef.current += 1;
    track('charging.pin.submitted', { metadata: { attempt: pinAttemptsRef.current } }); // count only, never the PIN
    // Clear any previous errors
    setError('');
    
    // Check if locked first
    if (isLocked) {
      setError(`PIN entry locked. Try again in ${formatLockoutTime(lockoutSeconds)}`);
      return;
    }
    
    // Validate plate
    if (!state.licensePlate || !state.licensePlate.trim()) {
      setError('No license plate provided.');
      return;
    }
    
    // Client-side validation before attempting verification
    if (!pin || pin.length === 0) {
      setError('Please enter your 4-digit PIN.');
      return;
    }
    
    if (pin.length < 4) {
      setError('PIN must be 4 digits.');
      return;
    }
    
    // Check for non-numeric characters (defensive, PinInput already filters)
    if (!/^\d+$/.test(pin)) {
      setError('PIN must be numbers only.');
      return;
    }
    
    // Trim to 4 digits if longer (defensive)
    const validPin = pin.slice(0, 4);
    
    // Reset cancellation flag before starting new verification
    verificationCancelledRef.current = false;
    setIsVerifying(true);
    
    console.log('[VehicleVerification] Verifying with API:', { 
      plate: state.licensePlate, 
      pinLength: validPin.length 
    });
    
    try {
      // Call real API: POST /api/vehicles/verify
      const result = await verifyVehiclePin({ 
        plate: state.licensePlate, 
        pin: validPin,
        stationId: state.stationId || undefined,
      });
      
      // GUARD: Check if verification was cancelled while request was in flight
      if (verificationCancelledRef.current) {
        console.log('[VehicleVerification] Verification cancelled - ignoring result');
        return; // Don't update state, user has navigated away
      }
      
      console.log('[VehicleVerification] API result:', { 
        verified: result.verified,
        billingMode: result.billingMode,
        hasError: !!result.error
      });
      
      setIsVerifying(false);
      
      // Check for network/server errors
      if (result.error) {
        if (result.error === PIN_VERIFY_RATE_LIMITED) {
          // Server-side lockout (plan v3.1 §9.3 E). The server's window is
          // authoritative; the local attempt counter is not advanced.
          const seconds = Number(result.retryAfterSeconds) > 0 ? Math.ceil(Number(result.retryAfterSeconds)) : 10 * 60;
          const message = pinRateLimitedMessage(result.retryAfterSeconds);
          setIsLocked(true);
          setLockoutSeconds(seconds);
          setPin('');
          setError(message);
          showToast({ message, type: 'error', duration: 6000 });
          return;
        }
        if (result.error === 'NETWORK_ERROR') {
          setError('Unable to connect. Please check your connection and try again.');
          showToast({
            message: 'Network error. Please try again.',
            type: 'error',
          });
        } else if (result.error === 'SERVER_ERROR') {
          setError('Service temporarily unavailable. Please try again.');
          showToast({
            message: 'Server error. Please try again.',
            type: 'error',
          });
        }
        // Do NOT decrement attempts on network/server errors
        return;
      }
      
      if (result.verified) {
        // Success - clear lockout and mark as verified
        clearLockout(plateForDisplay);

        // ── Charger access (platform cleanup, Phase 3) ──────────────
        // Ask the backend what this vehicle may do at this charger BEFORE
        // anything is created or attached. The app reads only whether the
        // outcome carries on (fleet session, or the ordinary card session)
        // or needs the result step (card fallback, denial, no charging
        // flow, or an outcome this build does not know). On the result step
        // no session is created with the vehicle and none is attached.
        //
        // A check that fails to answer never blocks charging: the backend
        // still decides at create / attach below.
        if (state.stationId && result.vehicleId) {
          setIsVerifying(true);
          let access;
          try {
            const { checkChargingAccess } = await import('../../../api/sessionsApi.js');
            access = await checkChargingAccess({
              stationId: state.stationId,
              vehicleId: result.vehicleId,
              vehiclePin: validPin,
            });
          } catch (accessError) {
            console.warn('[VehicleVerification] Access check unavailable — continuing', accessError?.message);
            access = { ok: false, error: 'ACCESS_CHECK_FAILED', httpStatus: null };
          }
          if (verificationCancelledRef.current) {
            console.log('[VehicleVerification] Verification cancelled - ignoring access check');
            return;
          }
          setIsVerifying(false);

          if (access?.error === 'VEHICLE_NOT_OWNED') {
            console.warn('[VehicleVerification] Vehicle not linked to this account');
            showVehicleNotOwned(access.message);
            return;
          }
          if (access?.ok && isAccessOutcome(access.accessOutcome)) {
            if (!accessOutcomeContinues(access.accessOutcome)) {
              console.log('[VehicleVerification] Access outcome needs the result step:', access.accessOutcome.outcome);
              showAccessResult(access.accessOutcome);
              return;
            }
          } else if (isAccessProofError(access)) {
            // The proof was refused: shown the way a PIN failure is.
            const proofMessage = proofErrorMessage(
              access.error,
              access.message || "We couldn't verify this vehicle. Please try again.",
            );
            setPin('');
            setError(proofMessage);
            showToast({ message: proofMessage, type: 'error' });
            return;
          } else {
            console.warn('[VehicleVerification] Access check gave no outcome — continuing', {
              error: access?.error,
              httpStatus: access?.httpStatus ?? null,
            });
          }
        }

        // The owner's contact (receipt fallback) is kept only for a guest. A
        // signed-in driver's receipt goes to their own account, and the
        // owner's name and address are never shown to someone else.
        const ownerContact = signedIn
          ? { ownerName: null, ownerEmail: null }
          : { ownerName: result.ownerName || null, ownerEmail: result.ownerEmail || null };

        console.log('[VehicleVerification] Verified successfully. Resolving billing rules:', {
          vehicleId: result.vehicleId,
          billingMode: result.billingMode,
          hasOwnerContact: Boolean(ownerContact.ownerEmail),
        });
        
        // Resolve vehicle charging rules using billing mode
        let chargingRules = resolveVehicleChargingRules(
          result, 
          state.pricingSnapshot, 
          state.stationMeta
        );
        
        console.log('[VehicleVerification] Charging rules resolved:', chargingRules);
        
        // Update session context with verified vehicle state
        // IMPORTANT: vehicle object is now the single source of truth
        update({
          // Legacy fields for compatibility
          vehicleVerified: true,
          verifiedPlate: result.plate || plateForDisplay,
          vehicleId: result.vehicleId,
          billingMode: chargingRules.billingMode,
          // Retired: the station / server rate is authoritative.
          rateOverride: null,
          
          // NEW: Unified vehicle object (single source of truth)
          vehicle: {
            licensePlateRaw: state.licensePlate || plateForDisplay,
            licensePlateNormalized: result.plate || plateForDisplay,
            vehicleId: result.vehicleId,
            verified: true,
            billingMode: chargingRules.billingMode,
            rateOverride: null,
            paymentRequired: chargingRules.paymentRequired,
            rateApplied: chargingRules.rateApplied,
            // Store owner contact info for receipt fallback
            ownerName: ownerContact.ownerName,
            ownerEmail: ownerContact.ownerEmail,
          },
        });
        
        // ── Session-existence boundary ─────────────────────────────
        // The saved-vehicle click path routes directly to this PIN
        // sub-step WITHOUT going through the ChargeSetup Continue
        // button, so state.sessionUid is null at this point. Both the
        // FREE/FLEET jump-to-CONNECT_CHARGER path AND the paid jump-
        // to-AMOUNT_PICKER path require a real UID downstream (SSE
        // ticket mint, /start, payment linking). Ensure it exists
        // NOW so no downstream effect has to guess.
        //
        // Forward the just-verified PIN so the backend can re-bind it
        // to THIS specific vehicleId in the guest flow (defence
        // against a swapped-vehicleId POST after /vehicles/verify).
        // The PIN never touches persistent state — it's captured from
        // the input in memory and passed straight through.
        //
        // The verified vehicleId is passed explicitly: the update() above has
        // not reached session state yet, and reading it from there created the
        // session with no vehicle_id at all.
        const ensured = await ensureCustomerSession({ vehiclePin: validPin, vehicleId: result.vehicleId });
        if (!ensured?.ok && ensured?.error === VEHICLE_ALREADY_CHARGING) {
          console.warn('[VehicleVerification] Vehicle already charging elsewhere (create)');
          showVehicleAlreadyCharging(ensured.message);
          return;
        }
        if (!ensured?.ok && ensured?.error === 'VEHICLE_NOT_OWNED') {
          // Never retried without the vehicle: that silently charged a
          // signed-in owner by card (incident 2026-10-14).
          console.warn('[VehicleVerification] Vehicle not linked to this account (create)');
          showVehicleNotOwned(ensured.message);
          return;
        }

        // The typed-plate journey created the session on the plate screen,
        // before this PIN step, so it exists without the vehicle. Bind the
        // verified vehicle to it now. A vehicle not linked to the signed-in
        // account stops here with its reason (showVehicleNotOwned); a network
        // failure leaves the session as it was, and charging continues.
        // The authorization the server applied for this vehicle (P4b):
        // on create (ensure) or on attach below.
        let appliedAuthorization = ensured?.ok && !ensured.alreadyExisted ? (ensured.authorization ?? null) : null;
        if (ensured?.ok && ensured.alreadyExisted && result.vehicleId) {
          const { attachSessionVehicle } = await import('../../../api/sessionsApi.js');
          const attached = await attachSessionVehicle(ensured.sessionUid, {
            vehicleId: result.vehicleId,
            vehiclePin: validPin,
          });
          if (!attached.ok && attached.error === VEHICLE_ALREADY_CHARGING) {
            console.warn('[VehicleVerification] Vehicle already charging elsewhere (attach)');
            showVehicleAlreadyCharging(attached.message);
            return;
          }
          if (!attached.ok && PIN_PROOF_ERRORS.has(attached.error)) {
            const proofMessage = proofErrorMessage(attached.error, attached.message || 'The vehicle PIN could not be confirmed.');
            console.warn('[VehicleVerification] Vehicle PIN refused on attach:', attached.error);
            setPin('');
            setError(proofMessage);
            showToast({ message: proofMessage, type: 'error' });
            return;
          }
          if (!attached.ok && attached.error === 'VEHICLE_NOT_OWNED') {
            console.warn('[VehicleVerification] Vehicle not linked to this account (attach)');
            showVehicleNotOwned(attached.message);
            return;
          }
          if (!attached.ok && attached.error === AUTHORIZATION_DENIED) {
            // Enforced refusal: the session stays as it was (not on this
            // vehicle's account), so do NOT advance on the vehicle's
            // billing mode — the driver can pay by card below.
            console.warn('[VehicleVerification] Vehicle attach not authorized:', attached.reasonCode);
            // Phase 3: a refusal that carries the access outcome goes to the
            // result step, which prints the backend's wording and actions.
            // The inline message + toast below (reason-code copy) is only
            // for an API that sends no accessOutcome.
            if (isAccessOutcome(attached.accessOutcome)) {
              showAccessResult(attached.accessOutcome);
              return;
            }
            setError(attached.message);
            showToast({ message: attached.message, type: 'error', duration: 6000 });
            return;
          }
          if (!attached.ok) {
            console.warn('[VehicleVerification] Verified vehicle not attached to the existing session:', attached.error);
          } else {
            appliedAuthorization = attached.authorization ?? null;
          }
        }
        if (!ensured?.ok && ensured?.error === AUTHORIZATION_DENIED) {
          console.warn('[VehicleVerification] Session start not authorized:', ensured.reasonCode);
          // Phase 3: as for attach above — the access outcome, when the
          // refusal carries one, is shown on the result step.
          if (isAccessOutcome(ensured.accessOutcome)) {
            showAccessResult(ensured.accessOutcome);
            return;
          }
          setError(ensured.message);
          showToast({ message: ensured.message, type: 'error', duration: 6000 });
          return;
        }

        // Under enforce the server's decision wins over the verify
        // response: a fleet vehicle may have fallen back to card here.
        const serverRules = enforcedBillingRules(appliedAuthorization);
        if (serverRules && (serverRules.billingMode !== chargingRules.billingMode
          || serverRules.paymentRequired !== chargingRules.paymentRequired)) {
          chargingRules = { ...chargingRules, ...serverRules };
          update({
            billingMode: serverRules.billingMode,
            vehicle: {
              licensePlateRaw: state.licensePlate || plateForDisplay,
              licensePlateNormalized: result.plate || plateForDisplay,
              vehicleId: result.vehicleId,
              verified: true,
              billingMode: serverRules.billingMode,
              rateOverride: null,
              paymentRequired: serverRules.paymentRequired,
              rateApplied: chargingRules.rateApplied,
              ownerName: ownerContact.ownerName,
              ownerEmail: ownerContact.ownerEmail,
            },
          });
        }
        if (appliedAuthorization) update({ authorization: appliedAuthorization });
        // The attach re-authorized the session: show the server tariff it
        // applied, not the card tariff captured when the session was created.
        const attachedPricing = ensured?.alreadyExisted ? pricingSnapshotFromAuthorization(appliedAuthorization) : null;
        if (attachedPricing) update({ pricingSnapshot: attachedPricing });
        if (appliedCardFallback(appliedAuthorization)) {
          showToast({ message: CARD_FALLBACK_MESSAGE, type: 'info', duration: 6000 });
        }
        if (!ensured?.ok || !ensured.sessionUid) {
          console.error('[VehicleVerification] Could not ensure a charging session — staying on PIN screen', {
            error: ensured?.error,
          });
          // Map guest-PIN backend errors to user-friendly copy that
          // NEVER surfaces the account-ownership language — the guest
          // flow is not an ownership check.
          const userMessage = proofErrorMessage(
            ensured?.error,
            ensured?.message || 'Could not start your charging session. Please try again.',
          );
          setError(userMessage);
          showToast({
            message: userMessage,
            type: 'error',
          });
          return;
        }
        const activeSessionUid = ensured.sessionUid;

        // Store inferred receipt contact for FREE/FLEET license plate
        // flow — safe now that we're guaranteed to have a sessionUid.
        // Fire-and-forget: not on the critical path.
        if (ownerContact.ownerEmail) {
          console.log('[FreeLicenseFlow] [VehicleVerification] Storing inferred receipt contact from vehicle owner');
          const { patchReceiptInferred } = await import('../../../api/sessionsApi.js');
          patchReceiptInferred(activeSessionUid, {
            email: ownerContact.ownerEmail,
            fullName: ownerContact.ownerName,
            source: 'vehicle_registry',
            billingMode: result.billingMode || null,
          })
            .then((patchResult) => {
              if (patchResult.ok) {
                console.log('[FreeLicenseFlow] [VehicleVerification] ✅ Inferred receipt contact stored (no confirmation email sent)');
              } else {
                console.warn('[FreeLicenseFlow] [VehicleVerification] ⚠️ Failed to store inferred receipt contact:', patchResult.error);
              }
            })
            .catch((err) => {
              console.error('[FreeLicenseFlow] [VehicleVerification] ❌ Exception storing inferred receipt:', err);
            });
        } else {
          console.log('[FreeLicenseFlow] [VehicleVerification] ℹ️ No owner email available - final receipt will require manual entry');
        }

        showToast({
          message: 'Vehicle verified successfully',
          type: 'success',
        });

        // CRITICAL FIX: Route based on FRESH chargingRules, not stale context state
        // The context update above is async and hasn't propagated yet!
        console.log('[VehicleVerification] ✅ Verification complete, routing based on fresh rules', {
          billingMode: chargingRules.billingMode,
          paymentRequired: chargingRules.paymentRequired,
        });

        if (!chargingRules.paymentRequired) {
          // FREE or FLEET_INVOICE - skip payment steps entirely
          console.log('[VehicleVerification] 🎯 No payment required - jumping directly to CONNECT_CHARGER (step 5)');
          // Step 2.5 (VEHICLE_VERIFICATION) → 5 (CONNECT_CHARGER)
          // Skipping: 3 (AMOUNT_PICKER) and 4 (MAKE_PAYMENT).
          // sessionUid is now guaranteed present in context.
          setStep(STEPS.CONNECT_CHARGER);
        } else {
          // PUBLIC_CARD or DISCOUNTED_CARD - proceed to AMOUNT_PICKER
          console.log('[VehicleVerification] 💳 Payment required - proceeding to AMOUNT_PICKER');
          handleIncreaseStep();
        }
      } else if (result.reason === 'charger_not_authorized_for_vehicle') {
        // Plate and PIN matched, but this vehicle is not allowed at this
        // charger. Not a wrong PIN: no attempt is used. The driver can still
        // charge by paying with a card (the link below).
        console.warn('[VehicleVerification] Vehicle not authorized for this charger');
        setPin('');
        setError("This vehicle isn't set up to charge here on its account. You can still charge by paying with a card.");
      } else {
        // Incorrect PIN (verified=false from API)
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        
        if (newAttempts >= MAX_ATTEMPTS) {
          // Lock out for 10 minutes
          setLockout(plateForDisplay);
          setIsLocked(true);
          setLockoutSeconds(10 * 60); // 10 minutes
          setError('Too many attempts. PIN entry is locked for 10 minutes. You can still charge by paying with a card.');
          setPin('');
          
          showToast({
            message: 'Too many attempts. You can still charge by paying with a card.',
            type: 'error',
            duration: 5000,
          });
          
          // Return to Charge Setup after a delay
          setTimeout(() => {
            handleDecreaseStep();
          }, 2000);
        } else {
          const remaining = MAX_ATTEMPTS - newAttempts;
          // The API answers `{ verified: false }` for an unknown plate AND
          // for a wrong PIN, deliberately — an asymmetric response would let
          // anyone enumerate which plates are registered by watching the
          // shape of the reply. See controllers/vehiclesPublicController.js.
          //
          // So this message must cover BOTH. It used to say "Incorrect
          // vehicle PIN", which told a driver whose plate was never
          // registered that their PIN was wrong — a PIN they do not have and
          // never had. They would burn all five attempts guessing and then be
          // locked out for ten minutes, with nothing on screen offering a way
          // to charge. The ambiguity belongs in the API; it must not become a
          // false accusation in the UI.
          setError(
            `We couldn't verify that plate and PIN. ${remaining} ${remaining === 1 ? 'attempt' : 'attempts'} remaining.`,
          );
          setPin('');
        }
      }
    } catch (err) {
      // GUARD: Check if verification was cancelled during error handling
      if (verificationCancelledRef.current) {
        console.log('[VehicleVerification] Verification cancelled - ignoring error');
        return;
      }
      
      console.error('[VehicleVerification] Unexpected error:', err);
      setIsVerifying(false);
      setError('An unexpected error occurred. Please try again.');
      showToast({
        message: 'Verification failed. Please try again.',
        type: 'error',
      });
      // Do NOT decrement attempts on unexpected errors
    }
  };
  
  // Handle back button
  const handleBack = () => {
    console.log('[VehicleVerification] Back button clicked', {
      isVerifying,
      hasPin: !!pin,
      hasError: !!error,
      plateWillBePreserved: !!state.licensePlate
    });
    
    // CRITICAL: Set cancellation flag to prevent stale async updates
    verificationCancelledRef.current = true;
    
    // Reset all PIN verification state
    resetPinVerificationState();
    
    // License plate is already stored in session state, will be preserved
    // Return to Charge Setup
    handleDecreaseStep();
  };
  
  const actions = [
    { 
      id: 'back', 
      label: 'Back', 
      type: 'btn-outline-dark',
      disabled: false, // Always allow Back navigation
    },
    { 
      id: 'verify', 
      label: isVerifying ? 'Verifying...' : 'Verify', 
      type: 'btn-dark',
      disabled: isVerifying, // Disabled while verifying to prevent duplicate requests
    },
  ];
  
  const handleActionClick = (actionId) => {
    if (actionId === 'back') {
      handleBack();
    } else if (actionId === 'verify') {
      handleVerify();
    }
  };
  
  return (
    <div className="ev-content-wrapper">
      <EvHeader
        headerAction={{
          label: 'Vehicle Verification',
          action: null,
        }}
      />

      <div className="ev-inner-profile-wrapper vehicle-verification-body">
        {/* Lockout Banner */}
        {isLocked && (
          <div className="lockout-banner">
            <div className="lockout-icon">🔒</div>
            <div className="lockout-content">
              <p className="lockout-message">Too many attempts. Try again in {formatLockoutTime(lockoutSeconds)}</p>
            </div>
          </div>
        )}

        {/* Main Content Card */}
        <div className="verification-card">
          {/* Header */}
          <div className="verification-header">
            <h1 className="verification-title">Enter vehicle PIN</h1>
            <p className="verification-subtitle">
              Enter the PIN for {state.selectedVehicle?.displayName || plateForDisplay || 'this vehicle'} to continue.
            </p>
            <div className="vehicle-badge">
              <span className="badge-label">Vehicle:</span>
              <span className="badge-value">{plateForDisplay}</span>
            </div>
          </div>

          {/* PIN Input Section */}
          <div className="pin-section">
            <label className="pin-label">Vehicle PIN</label>
            <PinInput
              value={pin}
              onChange={handlePinChange}
              disabled={isLocked || isVerifying}
              hasError={!!error}
              autoFocus={!isLocked}
              onSubmit={handleVerify}
            />
            {error ? (
              <p className="pin-error" role="alert">{error}</p>
            ) : (
              <p className="pin-helper">You have {MAX_ATTEMPTS - attempts} attempt{MAX_ATTEMPTS - attempts === 1 ? '' : 's'} remaining.</p>
            )}
          </div>

          {/*
            The way out for a plate that was never registered.

            Always present, not only after a failure: a driver who knows they
            have no PIN should not have to fail first to discover that they
            can still charge. It stays available during lockout, because the
            lockout is on PIN entry — not on charging.
          */}
          <div className="verification-escape">
            <button
              type="button"
              className="verification-escape-link"
              onClick={continueWithoutVehicle}
              disabled={isVerifying}
            >
              Don&rsquo;t have a vehicle PIN? Pay by card instead
            </button>
          </div>
        </div>
      </div>

      <ActionButtonWrapper
        ref={ref}
        actions={actions}
        onClickAction={handleActionClick}
      />
    </div>
  );
};

export default VehicleVerification;
